import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import * as Sentry from '@sentry/react';
import BEM from 'common/bem';
import type { ArchivingExportSummary } from 'js-sdk/src/apis/ArchivingAPI';

const classes = BEM.with('ExportHistory');

// Statuses that mean a job might still transition — while any row is in one of these, we poll for
// updates instead of requiring the user to manually refresh to see queued/processing become
// ready/failed.
const PENDING_STATUSES = new Set(['queued', 'processing']);
const POLL_INTERVAL_MS = 8000;
// After this many consecutive failed background polls, surface a notice — a single transient
// failure isn't worth alarming the user over, but a run of them means the row's real status is no
// longer being tracked and silently showing "Processing" forever would be misleading.
const POLL_FAILURE_NOTICE_THRESHOLD = 3;
export const EXPORT_HISTORY_PAGE_SIZE = 25;
// How many pages past the loaded ones a background re-walk may read to find rows pushed down by
// new exports (4 pages = up to 100 new exports between polls).
const EXPORT_REWALK_EXTRA_PAGES = 4;

type ExportHistoryClient = {
  archiving: {
    listExports: (
      organizationId: string,
      options?: { pageSize?: number; nextToken?: string }
    ) => Promise<{ data: { exports: ArchivingExportSummary[]; nextToken?: string | null } }>;
    getExportDownloadUrl: (
      organizationId: string,
      jobId: string
    ) => Promise<{ data: { downloadUrl: string } }>;
  };
};

type ExportHistoryProps = {
  client: ExportHistoryClient | null;
  currentOrganizationId: string;
};

function statusLabel(status: string): string {
  switch (status) {
    case 'queued':
      return 'Queued';
    case 'processing':
      return 'Processing';
    case 'ready':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

export default function ExportHistory({ client, currentOrganizationId }: ExportHistoryProps) {
  const [exports, setExports] = useState<ArchivingExportSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [pollFailureNotice, setPollFailureNotice] = useState(false);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const hasPendingExports = useRef(false);
  const consecutivePollFailures = useRef(0);
  // Bumped on every foreground (re)load so a slower earlier response — first page or load-more —
  // can't overwrite or append to a newer list.
  const loadSeq = useRef(0);
  // Whether the user has loaded any page past the first. Background polls only ever re-fetch the
  // first page, so once extra pages are loaded they must keep the existing nextToken (which points
  // past the last loaded page) rather than resetting it to the first page's.
  const hasLoadedExtraPages = useRef(false);
  // Mirrors of the loaded list and page count so a background poll can tell whether a pending job
  // sits beyond the first page and how much of the list it has to re-walk to refresh it.
  const exportsRef = useRef<ArchivingExportSummary[]>([]);
  const loadedPageCount = useRef(0);
  const nextTokenRef = useRef<string | null>(null);

  const applyExports = useCallback(
    (list: ArchivingExportSummary[], token: string | null, pageCount: number) => {
      exportsRef.current = list;
      loadedPageCount.current = pageCount;
      nextTokenRef.current = token;
      hasPendingExports.current = list.some((item) => PENDING_STATUSES.has(item.jobStatus));
      setExports(list);
      setNextToken(token);
    },
    []
  );

  const loadExports = useCallback(
    async (isBackgroundRefresh = false) => {
      if (!client) return;

      let seq = loadSeq.current;
      if (!isBackgroundRefresh) {
        loadSeq.current += 1;
        seq = loadSeq.current;
        setIsLoading(true);
        setError(null);
        setLoadMoreError(null);
        setIsLoadingMore(false);
      }
      try {
        const response = await client.archiving.listExports(currentOrganizationId, {
          pageSize: EXPORT_HISTORY_PAGE_SIZE,
        });
        if (seq !== loadSeq.current) return;
        const results = response?.data?.exports || [];
        const firstPageToken = response?.data?.nextToken || null;
        if (isBackgroundRefresh && hasLoadedExtraPages.current) {
          const freshIds = new Set(results.map((item) => item.exportJobId));
          const previous = exportsRef.current;
          const hasPendingBeyondFirstPage = previous.some(
            (item) => PENDING_STATUSES.has(item.jobStatus) && !freshIds.has(item.exportJobId)
          );
          if (hasPendingBeyondFirstPage) {
            // A pending job lives on a later loaded page, which a first-page fetch can't refresh.
            // Re-walk from the start until every previously loaded row has been seen again — not
            // just the same number of rows, since new exports push old rows further down — bounded
            // so a fast-growing list can't walk forever.
            const unseenIds = new Set(
              previous.map((item) => item.exportJobId).filter((id) => !freshIds.has(id))
            );
            const maxPages = loadedPageCount.current + EXPORT_REWALK_EXTRA_PAGES;
            const walked = [...results];
            const walkedIds = new Set(freshIds);
            let token = firstPageToken;
            let pages = 1;
            while (token && unseenIds.size > 0 && pages < maxPages) {
              const pageResponse = await client.archiving.listExports(currentOrganizationId, {
                pageSize: EXPORT_HISTORY_PAGE_SIZE,
                nextToken: token,
              });
              if (seq !== loadSeq.current) return;
              for (const item of pageResponse?.data?.exports || []) {
                unseenIds.delete(item.exportJobId);
                if (!walkedIds.has(item.exportJobId)) {
                  walkedIds.add(item.exportJobId);
                  walked.push(item);
                }
              }
              token = pageResponse?.data?.nextToken || null;
              pages += 1;
            }
            if (token && unseenIds.size > 0) {
              // Hit the bound before reaching every loaded row: keep the rows not re-seen (with
              // their last known status) and the old cursor, which still points past them.
              walked.push(...previous.filter((item) => unseenIds.has(item.exportJobId)));
              token = nextTokenRef.current;
            }
            hasLoadedExtraPages.current = pages > 1;
            applyExports(walked, token, pages);
          } else {
            // Cheaper path: refresh the first page in place and keep every row from the extra
            // pages the user already loaded (minus any that the fresh first page now contains),
            // along with the existing nextToken, which points past the last loaded page.
            applyExports(
              [...results, ...previous.filter((item) => !freshIds.has(item.exportJobId))],
              nextTokenRef.current,
              loadedPageCount.current
            );
          }
        } else {
          hasLoadedExtraPages.current = false;
          applyExports(results, firstPageToken, 1);
        }
        consecutivePollFailures.current = 0;
        setPollFailureNotice(false);
      } catch (err) {
        if (seq !== loadSeq.current) return;
        if (!isBackgroundRefresh) {
          setError('Something went wrong loading export history. Please try again.');
          return;
        }

        // A background poll fails silently to the user by design for the first couple of
        // attempts (a single blip shouldn't alarm anyone), but it must never fail silently to
        // telemetry — without this, a status stuck on "queued"/"processing" forever because
        // polling itself broke looks identical to a job that's genuinely still processing.
        consecutivePollFailures.current += 1;
        Sentry.logger.warn(
          Sentry.logger.fmt`[ExportHistory] Background export status poll failed`,
          { error: err, consecutiveFailures: consecutivePollFailures.current }
        );
        if (consecutivePollFailures.current >= POLL_FAILURE_NOTICE_THRESHOLD) {
          setPollFailureNotice(true);
        }
      } finally {
        if (!isBackgroundRefresh && seq === loadSeq.current) {
          setIsLoading(false);
        }
      }
    },
    [applyExports, client, currentOrganizationId]
  );

  useEffect(() => {
    loadExports();
  }, [loadExports]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (hasPendingExports.current) {
        loadExports(true);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadExports]);

  async function onLoadMore() {
    if (!client || !nextToken) return;

    // Bump the sequence so an in-flight background poll (which may be re-walking pages) can't
    // replace the list underneath this append.
    loadSeq.current += 1;
    const seq = loadSeq.current;
    setIsLoadingMore(true);
    setLoadMoreError(null);
    try {
      const response = await client.archiving.listExports(currentOrganizationId, {
        pageSize: EXPORT_HISTORY_PAGE_SIZE,
        nextToken,
      });
      if (seq !== loadSeq.current) return;
      const page = response?.data?.exports || [];
      hasLoadedExtraPages.current = true;
      const previous = exportsRef.current;
      const existingIds = new Set(previous.map((item) => item.exportJobId));
      applyExports(
        [...previous, ...page.filter((item) => !existingIds.has(item.exportJobId))],
        response?.data?.nextToken || null,
        loadedPageCount.current + 1
      );
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setLoadMoreError('Something went wrong loading more exports. Please try again.');
    } finally {
      if (seq === loadSeq.current) setIsLoadingMore(false);
    }
  }

  async function onDownload(jobId: string) {
    if (!client) return;

    setDownloadingJobId(jobId);
    setDownloadError(null);
    try {
      const response = await client.archiving.getExportDownloadUrl(currentOrganizationId, jobId);
      const downloadUrl = response?.data?.downloadUrl;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setDownloadError('Something went wrong generating the download link. Please try again.');
    } finally {
      setDownloadingJobId(null);
    }
  }

  return (
    <div className={classes()}>
      <div className={classes('header')}>
        <div className={classes('header-text')}>
          <h2 className={classes('title')}>Export History</h2>
          <p className={classes('subtitle')}>
            Track and download exports triggered for this organization
          </p>
        </div>
        <button
          className={classes('refresh-button')}
          data-test-id="export-history-refresh"
          disabled={isLoading}
          onClick={() => loadExports()}
          type="button"
        >
          Refresh
        </button>
      </div>

      {error && <div className={classes('error')}>{error}</div>}
      {downloadError && <div className={classes('error')}>{downloadError}</div>}
      {pollFailureNotice && (
        <div className={classes('poll-failure-notice')} data-test-id="export-history-poll-failure">
          Having trouble checking for status updates. Click Refresh to check manually.
        </div>
      )}

      {isLoading && (
        <div className={classes('loading')} data-test-id="export-history-loading">
          Loading export history…
        </div>
      )}

      {!isLoading && !error && exports.length === 0 && (
        <div className={classes('empty-state')} data-test-id="export-history-empty">
          {nextToken
            ? 'No exports in the pages checked so far. Load more to keep looking.'
            : 'No exports yet. Exports you generate from Export Builder will show up here.'}
          {nextToken && (
            <div>
              <button
                className={classes('load-more-button')}
                data-test-id="export-history-load-more"
                disabled={isLoadingMore}
                onClick={onLoadMore}
                type="button"
              >
                {isLoadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}

      {!isLoading && exports.length > 0 && (
        <ul className={classes('list')} data-test-id="export-history-list">
          {exports.map((item) => (
            <li className={classes('item')} key={item.exportJobId}>
              <div className={classes('item-main')}>
                <span className={classes('item-name')}>{item.exportName || item.exportJobId}</span>
                <span className={classes('item-meta')}>
                  Created {moment(item.createdAt).format('MMM D, YYYY h:mm A')} · Expires{' '}
                  {moment(item.expiresAt).format('MMM D, YYYY h:mm A')}
                </span>
              </div>
              <div className={classes('item-actions')}>
                <span
                  className={classes('status-badge', { [item.jobStatus]: true })}
                  data-test-id={`export-history-status-${item.exportJobId}`}
                >
                  {statusLabel(item.jobStatus)}
                </span>
                {item.jobStatus === 'ready' && (
                  <button
                    className={classes('download-button')}
                    data-test-id={`export-history-download-${item.exportJobId}`}
                    disabled={downloadingJobId === item.exportJobId}
                    onClick={() => onDownload(item.exportJobId)}
                    type="button"
                  >
                    {downloadingJobId === item.exportJobId ? 'Preparing…' : 'Download'}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {!isLoading && !error && exports.length > 0 && (
        <div className={classes('footer')}>
          <span className={classes('count')} data-test-id="export-history-count">
            Showing {exports.length} export{exports.length === 1 ? '' : 's'}
          </span>
          {nextToken && (
            <button
              className={classes('load-more-button')}
              data-test-id="export-history-load-more"
              disabled={isLoadingMore}
              onClick={onLoadMore}
              type="button"
            >
              {isLoadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}
      {loadMoreError && <div className={classes('error')}>{loadMoreError}</div>}
    </div>
  );
}
